import * as React from "react";
import { cn } from "@/lib/utils";
import {
  PairingIcon,
  ClothesIcon,
  ArrowDownIcon,
  NfcWirelessIcon,
  NotificationIcon,
  UnpairIcon,
  CheckCircleIcon,
} from "./icons";

export interface PairingHelpViewProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Pairing Help View component
 *
 * Displays step-by-step instructions for pairing NFC products with the ANYW3AR app.
 * Shows 5 instructional steps with icons and explanatory text.
 *
 * @example
 * ```tsx
 * <PairingHelpView />
 * ```
 */
export const PairingHelpView = React.forwardRef<
  HTMLDivElement,
  PairingHelpViewProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-8 items-start pt-2 size-full",
        className
      )}
      {...props}
    >
      {/* Instruction Steps */}
      <div className="flex flex-col gap-[0.625rem] items-start w-full">
        {/* Step 1: NFC Technology */}
        <div className="border border-gray-300 border-solid rounded-[0.3125rem] w-full overflow-hidden">
          <div className="flex items-stretch">
            <div className="bg-gray-500 flex items-center justify-center p-2 w-[2.5rem] shrink-0">
              <PairingIcon size={16} className="text-white" />
            </div>
            <div className="flex flex-1 items-center justify-center p-2 min-w-0">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                To scan a product, use a phone equipped with NFC technology.
              </p>
            </div>
          </div>
        </div>

        {/* Step 2: Bring Phone Close */}
        <div className="border border-gray-300 border-solid rounded-[0.3125rem] w-full overflow-hidden">
          <div className="flex items-stretch">
            <div className="bg-gray-500 flex flex-col gap-[0.1875rem] items-center justify-center p-2 w-[2.5rem] shrink-0">
              <ClothesIcon size={20} className="text-white" />
              <div className="flex flex-col gap-1 items-center justify-center w-full">
                <div className="rotate-90">
                  <ArrowDownIcon
                    size={10}
                    weight="medium"
                    className="text-white"
                  />
                </div>
                <NfcWirelessIcon
                  size={16}
                  weight="regular"
                  className="text-white"
                />
              </div>
            </div>
            <div className="flex flex-1 items-center justify-center p-2 min-w-0">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                Bring your phone close to your product's chip (located on the
                ANYW3AR label). This isn't a QR code — no camera needed.
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Notification Appears */}
        <div className="border border-gray-300 border-solid rounded-[0.3125rem] w-full overflow-hidden">
          <div className="flex items-stretch">
            <div className="bg-gray-500 flex flex-col gap-[0.375rem] items-center justify-center p-2 w-[2.5rem] shrink-0">
              <NotificationIcon
                size={16}
                weight="regular"
                className="text-white"
              />
              {/* ANYW3AR logo */}
              <svg
                width="30"
                height="13"
                viewBox="0 0 30 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0"
              >
                <path
                  d="M27.538 4.62406L28.7712 2.30779L30 0H4.70917L4.3453 1.00459L0.761711 10.8973L0 13H24.4442C25.6712 13 26.8273 12.4831 27.6993 11.5446C28.5546 10.6239 29.0257 9.41738 29.0257 8.14715C29.0257 7.16523 28.741 6.22098 28.2025 5.41639C28.0076 5.12533 27.7848 4.8602 27.538 4.62406ZM9.32251 11.4464L6.47664 3.59027L3.63088 11.4464H2.15523L5.73892 1.55354H7.21457L10.7982 11.4464H9.32251ZM20.5406 11.4464H19.065L16.2192 6.41261L13.3734 11.4464H11.8978V1.55354H13.3734V8.83628L15.4814 5.10757H16.957L19.0651 8.83628V1.55354H20.5407L20.5406 11.4464ZM24.4441 11.4464H21.9325V9.94683H24.4441C25.3547 9.94683 26.0954 9.09661 26.0954 8.14705C26.0954 7.19749 25.3547 6.42496 24.4441 6.42496H23.2117L25.0039 3.10748H22.0912V1.55343H27.4688L25.5593 5.13962C26.7147 5.6072 27.5358 6.77891 27.5358 8.14705C27.5358 9.92498 26.1489 11.4463 24.444 11.4463L24.4441 11.4464Z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="flex flex-1 items-center justify-center p-2 min-w-0">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                A notification appears on the phone, redirecting you to the
                ANYW3AR dApp pairing page.
              </p>
            </div>
          </div>
        </div>

        {/* Step 4: Enter Pairing Code */}
        <div className="border border-gray-300 border-solid rounded-[0.3125rem] w-full overflow-hidden">
          <div className="flex items-stretch">
            <div className="bg-gray-500 flex items-center justify-center p-2 w-[2.5rem] shrink-0">
              <UnpairIcon size={16} className="text-white" />
            </div>
            <div className="flex flex-1 items-center justify-center p-2 min-w-0">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                Enter the pairing code you received by e-mail following your
                purchase.
              </p>
            </div>
          </div>
        </div>

        {/* Step 5: Product Registered */}
        <div className="border border-gray-300 border-solid rounded-[0.3125rem] w-full overflow-hidden">
          <div className="flex items-stretch">
            <div className="bg-gray-500 flex items-center justify-center p-2 w-[2.5rem] shrink-0">
              <CheckCircleIcon size={16} weight="bold" className="text-white" />
            </div>
            <div className="flex flex-1 items-center justify-center p-2 min-w-0">
              <p className="font-montserrat font-normal text-[0.9375rem] leading-5 text-gray-500">
                And your product is registered on your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PairingHelpView.displayName = "PairingHelpView";
